import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { robot } from 'lib/icons';
import { Flex } from '@chakra-ui/react';
import { getShift } from 'lib/selectors/getShift';
import { ShiftIndicatorIcon } from './styled';

/**
 * ShiftIndicator Component
 *
 * Displays the current shift status of a machine, indicating whether:
 * - The machine is not in a shift schedule (shows robot icon with "Not in Shift Schedule")
 * - The machine is in an unattended shift (shows robot icon with "Unattended Shift")
 * - The machine is in an attended shift (renders nothing)
 *
 * @component
 * @returns {JSX.Element|null} The rendered shift indicator component or null for attended shifts
 *
 * @example
 * return (
 *   <ShiftIndicator />
 * )
 */
const ShiftIndicator = () => {
  const { t } = useTranslation();
  const shift = useSelector(getShift);

  // Show empty container for attended shifts
  if (shift?.attended) {
    return null;
  }

  // Check if we have actual shift data (not just empty default object)
  const hasShiftData = shift?.startAt && shift?.name;

  // Show indicator with appropriate text for no shift or unattended shift
  const text = hasShiftData
    ? t('Unattended Shift')
    : t('Not in Shift Schedule');

  return (
    <Flex alignItems="center" gap="0.5rem" justifyContent="center">
      <ShiftIndicatorIcon icon={robot} />
      <span>{text}</span>
    </Flex>
  );
};

export default ShiftIndicator;
